import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Building2, Search, Plus, Filter, Users, Globe, Phone, 
  Mail, Calendar, Eye, CheckCircle, X, Shield, ArrowUpRight,
  Sparkles, Lock, RefreshCw, Copy, Check, Radio, Award, Clock, Send, CheckCircle2
} from 'lucide-react';
import { bdAPI } from '../utils/api';

const BDAgencies = () => {
  const location = useLocation();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [copiedId, setCopiedId] = useState(null);

  // Invite Agency Modal & History
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ identifier: '', agency_name: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteErrorMsg, setInviteErrorMsg] = useState('');
  const [inviteSuccessMsg, setInviteSuccessMsg] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // Onboard Agency Modal
  const [isOnboardModalOpen, setIsOnboardModalOpen] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    agency_name: '',
    real_name: '',
    email: '',
    whatsapp_number: '',
    password: '',
    country: 'India',
    experience: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Agency Detail Modal (Drill-Down 1: Agency -> Hosts)
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyHosts, setAgencyHosts] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Host Center Detail Modal (Drill-Down 2: Host -> Sessions & Live Stats)
  const [selectedHost, setSelectedHost] = useState(null);
  const [hostSessions, setHostSessions] = useState([]);
  const [loadingHostDetail, setLoadingHostDetail] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const res = await bdAPI.getAgencies();
      if (res.data?.success) {
        setAgencies(res.data.agencies || []);
      }
    } catch (err) {
      console.error("Error fetching agencies:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = async (agency) => {
    setSelectedAgency(agency);
    setLoadingDetail(true);
    try {
      const res = await bdAPI.getAgency(agency.id);
      if (res.data?.success) {
        setAgencyHosts(res.data.hosts || []);
      }
    } catch (err) {
      console.error("Error loading agency details:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleOpenHostDetails = async (hostId) => {
    setLoadingHostDetail(true);
    try {
      const res = await bdAPI.getHostDetails(hostId);
      if (res.data?.success) {
        setSelectedHost(res.data.host);
        setHostSessions(res.data.sessions || []);
      }
    } catch (err) {
      console.error("Error loading host details:", err);
    } finally {
      setLoadingHostDetail(false);
    }
  };

  const handleCreateAgency = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const res = await bdAPI.createAgency(onboardForm);
      if (res.data?.success) {
        setSuccessMsg(res.data.message || 'Agency onboarded successfully under your portfolio!');
        setOnboardForm({
          agency_name: '',
          real_name: '',
          email: '',
          whatsapp_number: '',
          password: '',
          country: 'India',
          experience: ''
        });
        setTimeout(() => {
          setIsOnboardModalOpen(false);
          setSuccessMsg('');
          fetchAgencies();
        }, 1200);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to onboard agency');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoadingInvitations(true);
      const res = await bdAPI.getInvitations();
      if (res.data?.success) {
        setInvitations(res.data.invitations || []);
      }
    } catch (err) {
      console.error("Error fetching invitations:", err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'invite') {
      setIsInviteModalOpen(true);
      fetchInvitations();
    }
  }, [location.search]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.identifier.trim()) {
      setInviteErrorMsg('Please enter Target User ID or XoXo ID');
      return;
    }
    setInviting(true);
    setInviteErrorMsg('');
    setInviteSuccessMsg('');
    try {
      const res = await bdAPI.inviteAgency({
        target_user_id: inviteForm.identifier.trim(),
        xoxo_id: inviteForm.identifier.trim(),
        agency_name: inviteForm.agency_name.trim()
      });
      if (res.data?.success) {
        setInviteSuccessMsg(res.data.message || 'Invitation sent successfully!');
        setInviteForm({ identifier: '', agency_name: '' });
        fetchInvitations();
      } else {
        setInviteErrorMsg(res.data?.message || 'Failed to send invitation');
      }
    } catch (err) {
      setInviteErrorMsg(err.response?.data?.message || err.message || 'Error sending invitation');
    } finally {
      setInviting(false);
    }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredAgencies = agencies.filter(a => {
    const matchesSearch = 
      a.agency_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.agency_code?.toString().includes(searchTerm) ||
      a.real_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.country?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'ALL' || 
      (statusFilter === 'ACTIVE' && (a.status === 'approved' || !a.status)) ||
      (statusFilter === 'PENDING' && a.status === 'pending');

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header and Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
            <Building2 size={16} />
            <span>Dedicated BD Portfolio</span>
          </div>
          <h2 className="text-3xl font-black text-white tracking-tight">Managed Agencies & Host Center</h2>
          <p className="text-slate-400 text-sm mt-1">
            Agencies under your Business Development wing. Drill down into any agency to see all hosts, target levels, valid live days, and commission earnings.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => fetchAgencies()}
            className="p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-colors cursor-pointer"
            title="Refresh list"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => {
              setIsInviteModalOpen(true);
              setInviteErrorMsg('');
              setInviteSuccessMsg('');
              fetchInvitations();
            }}
            className="px-5 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg shadow-purple-500/20 flex items-center gap-2 transition-all hover:scale-105 cursor-pointer"
          >
            <Mail size={18} />
            <span>Invite Agency</span>
          </button>
          <button
            onClick={() => setIsOnboardModalOpen(true)}
            className="px-5 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 flex items-center gap-2 transition-all hover:scale-105 cursor-pointer"
          >
            <Plus size={18} />
            <span>Onboard New Agency</span>
          </button>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search by Agency Name, Code, Manager Name or Country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#0f172a] border border-white/10 rounded-2xl py-3.5 pl-12 pr-4 text-white text-sm outline-none focus:border-emerald-500/50"
          />
        </div>
        <div className="flex items-center gap-2">
          {['ALL', 'ACTIVE', 'PENDING'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-3 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
                statusFilter === status 
                  ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-md shadow-emerald-500/20' 
                  : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Agencies Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="w-8 h-8 border-3 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
      ) : filteredAgencies.length === 0 ? (
        <div className="p-12 text-center bg-white/[0.02] border border-white/5 rounded-[32px]">
          <Building2 size={40} className="mx-auto text-slate-600 mb-3" />
          <p className="text-white font-bold text-base">No agencies found</p>
          <p className="text-slate-400 text-xs mt-1">Try refining your search or onboard a new agency.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgencies.map((agency) => (
            <div 
              key={agency.id}
              className="bg-[#0f172a] border border-white/10 rounded-[28px] p-6 hover:border-emerald-500/30 transition-all duration-300 relative group flex flex-col justify-between"
            >
              <div>
                {/* Card Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-black text-lg shadow-md shadow-emerald-500/20">
                      {agency.agency_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base leading-snug">{agency.agency_name}</h4>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[11px] font-mono text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                          #{agency.agency_code}
                        </span>
                        <button
                          onClick={() => copyCode(agency.agency_code, agency.id)}
                          className="text-slate-400 hover:text-white p-1"
                          title="Copy Code"
                        >
                          {copiedId === agency.id ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    agency.status === 'approved' || !agency.status 
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                      : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  }`}>
                    {agency.status === 'approved' || !agency.status ? 'Active' : agency.status}
                  </span>
                </div>

                {/* Manager / Details */}
                <div className="space-y-1.5 text-xs text-slate-300 py-3 border-y border-white/5 my-3">
                  <p className="flex items-center justify-between">
                    <span className="text-slate-500">Manager:</span>
                    <span className="font-semibold text-white">{agency.real_name || 'N/A'}</span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="text-slate-500">WhatsApp:</span>
                    <span className="font-mono text-slate-300">{agency.whatsapp_number || 'N/A'}</span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="text-slate-500">Country:</span>
                    <span>{agency.country || 'India'}</span>
                  </p>
                </div>

                {/* Performance Metrics: Hosts, Target Coins, BD Commission */}
                <div className="grid grid-cols-3 gap-2 my-4 text-center">
                  <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
                    <span className="text-[10px] text-slate-400 font-bold uppercase block">Hosts</span>
                    <span className="text-base font-black text-white">{agency.active_hosts_count || 0}</span>
                  </div>
                  <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
                    <span className="text-[10px] text-slate-400 font-bold uppercase block">Target Coins</span>
                    <span className="text-xs font-bold text-amber-400 font-mono block mt-1">
                      {Number(agency.monthly_target_coins || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
                    <span className="text-[10px] text-emerald-400 font-bold uppercase block">Your BD Comm</span>
                    <span className="text-xs font-bold text-emerald-400 font-mono block mt-1">
                      💎{Number(agency.total_bd_commission_diamonds || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button: Drill Down */}
              <button
                onClick={() => handleOpenDetail(agency)}
                className="w-full mt-2 py-3 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white font-bold text-xs flex items-center justify-center gap-2 border border-white/10 transition-colors cursor-pointer"
              >
                <Eye size={15} className="text-emerald-400" />
                <span>Drill Down to Agency Hosts ({agency.active_hosts_count || 0})</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Onboard Agency Modal */}
      {isOnboardModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-8 max-w-lg w-full shadow-2xl relative">
            <button
              onClick={() => setIsOnboardModalOpen(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <Building2 size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Onboard New Agency</h3>
                <p className="text-xs text-slate-400">Add an agency directly under your BD profile</p>
              </div>
            </div>

            {errorMsg && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-semibold">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-xs font-semibold">
                {successMsg}
              </div>
            )}

            <form onSubmit={handleCreateAgency} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5">Agency Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Star Light Agency"
                  value={onboardForm.agency_name}
                  onChange={(e) => setOnboardForm({ ...onboardForm, agency_name: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1.5">Manager / Real Name</label>
                  <input
                    type="text"
                    placeholder="Manager full name"
                    value={onboardForm.real_name}
                    onChange={(e) => setOnboardForm({ ...onboardForm, real_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1.5">WhatsApp / Phone</label>
                  <input
                    type="text"
                    placeholder="+91..."
                    value={onboardForm.whatsapp_number}
                    onChange={(e) => setOnboardForm({ ...onboardForm, whatsapp_number: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1.5">Agency Login Email</label>
                  <input
                    type="email"
                    placeholder="agency@example.com"
                    value={onboardForm.email}
                    onChange={(e) => setOnboardForm({ ...onboardForm, email: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 block mb-1.5">Password</label>
                  <input
                    type="password"
                    placeholder="Default: xoxo@123"
                    value={onboardForm.password}
                    onChange={(e) => setOnboardForm({ ...onboardForm, password: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5">Country</label>
                <select
                  value={onboardForm.country}
                  onChange={(e) => setOnboardForm({ ...onboardForm, country: e.target.value })}
                  className="w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-emerald-500"
                >
                  <option value="India">India</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="Bangladesh">Bangladesh</option>
                  <option value="USA">USA</option>
                  <option value="UAE">UAE</option>
                  <option value="Indonesia">Indonesia</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                <button
                  type="button"
                  onClick={() => setIsOnboardModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-white/10 text-slate-400 hover:text-white text-sm font-semibold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? 'Registering...' : 'Confirm & Onboard'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* INVITE AGENCY PARTNER MODAL & SENT INVITATIONS */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f172a] border border-purple-500/30 rounded-[32px] p-6 md:p-8 max-w-2xl w-full shadow-2xl relative max-h-[90vh] flex flex-col">
            <button
              onClick={() => setIsInviteModalOpen(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>

            {/* Modal Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-purple-500/20 shrink-0">
                <Mail size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Invite Agency Partner</h3>
                <p className="text-slate-400 text-xs mt-0.5">
                  Send an official Agency Partner invitation to any user. Once accepted, their agency will link directly to your BD portfolio.
                </p>
              </div>
            </div>

            {/* Alert Messages */}
            {inviteErrorMsg && (
              <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-semibold">
                {inviteErrorMsg}
              </div>
            )}
            {inviteSuccessMsg && (
              <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold">
                {inviteSuccessMsg}
              </div>
            )}

            {/* Invitation Form */}
            <form onSubmit={handleSendInvite} className="space-y-4 mb-6">
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5">
                  Target User XoXo ID / User ID <span className="text-rose-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="Enter target user's 8-digit XoXo ID or User ID"
                    value={inviteForm.identifier}
                    onChange={(e) => setInviteForm({ ...inviteForm, identifier: e.target.value })}
                    className="w-full bg-[#0a0f1d] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-purple-500 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1.5">
                  Proposed Agency Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Royal Star Agency (User can customize later)"
                  value={inviteForm.agency_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, agency_name: e.target.value })}
                  className="w-full bg-[#0a0f1d] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-purple-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="submit"
                  disabled={inviting}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  <Send size={16} />
                  <span>{inviting ? 'Sending Invitation...' : 'Send Official Invitation'}</span>
                </button>
              </div>
            </form>

            {/* Sent Invitations History */}
            <div className="border-t border-white/10 pt-4 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock size={14} className="text-purple-400" />
                  <span>Sent Invitations History ({invitations.length})</span>
                </h4>
                <button
                  type="button"
                  onClick={fetchInvitations}
                  className="text-xs text-purple-400 hover:text-purple-300 cursor-pointer flex items-center gap-1"
                >
                  <RefreshCw size={12} className={loadingInvitations ? 'animate-spin' : ''} />
                  <span>Refresh</span>
                </button>
              </div>

              {loadingInvitations ? (
                <div className="text-center py-6 text-xs text-slate-500">Loading invitations...</div>
              ) : invitations.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-500 bg-white/[0.01] rounded-xl border border-white/5">
                  No agency invitations sent yet. Enter a user ID above to send one.
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {invitations.map((inv) => (
                    <div
                      key={inv.id}
                      className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-300 flex items-center justify-center font-bold">
                          {inv.target_first_name ? inv.target_first_name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                          <p className="font-bold text-white">
                            {inv.target_first_name ? `${inv.target_first_name} ${inv.target_last_name || ''}` : inv.target_user_name || 'User'}
                            <span className="text-slate-400 font-normal ml-1">
                              ({inv.agency_name || 'Agency'})
                            </span>
                          </p>
                          <p className="text-[11px] font-mono text-slate-400">
                            XoXo ID: <span className="text-purple-300">{inv.target_xoxo_id || 'N/A'}</span> • Code: <span className="text-slate-300">{inv.invite_code}</span>
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            inv.status === 'accepted'
                              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                              : inv.status === 'rejected'
                              ? 'bg-rose-500/15 text-rose-400 border border-rose-500/20'
                              : 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
                          }`}
                        >
                          {inv.status || 'pending'}
                        </span>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          {new Date(inv.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DRILL-DOWN 1: AGENCY DETAILS & HOSTS LIST */}
      {selectedAgency && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-6 md:p-8 max-w-3xl w-full shadow-2xl relative max-h-[90vh] flex flex-col">
            <button
              onClick={() => setSelectedAgency(null)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>

            {/* Modal Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-black text-xl shadow-lg shadow-emerald-500/20">
                {selectedAgency.agency_name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">{selectedAgency.agency_name}</h3>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                  <span className="font-mono text-emerald-400 font-bold">#{selectedAgency.agency_code}</span>
                  <span>•</span>
                  <span>Manager: {selectedAgency.real_name || 'N/A'}</span>
                  <span>•</span>
                  <span>{selectedAgency.country || 'India'}</span>
                </div>
              </div>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 text-center">
                <span className="text-[11px] font-bold text-slate-400 uppercase">Total Hosts</span>
                <p className="text-xl font-black text-white mt-1">{agencyHosts.length}</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 text-center">
                <span className="text-[11px] font-bold text-slate-400 uppercase">Month Coins</span>
                <p className="text-xl font-black text-amber-400 mt-1">
                  {Number(selectedAgency.monthly_target_coins || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 text-center">
                <span className="text-[11px] font-bold text-emerald-400 uppercase">Your BD Commission</span>
                <p className="text-xl font-black text-emerald-400 mt-1">
                  💎{Number(selectedAgency.total_bd_commission_diamonds || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Hosts under this Agency */}
            <div className="flex-1 overflow-y-auto pr-1">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users size={16} className="text-emerald-400" />
                  <span>Hosts in this Agency ({agencyHosts.length})</span>
                </span>
                <span className="text-[11px] text-slate-400 font-normal">Click any host to see their Live Sessions & Host Center History</span>
              </h4>

              {loadingDetail ? (
                <div className="p-8 text-center text-slate-400">Loading hosts...</div>
              ) : agencyHosts.length === 0 ? (
                <div className="p-8 text-center text-slate-400 bg-white/[0.02] rounded-2xl border border-white/5">
                  No hosts currently assigned to this agency.
                </div>
              ) : (
                <div className="space-y-3">
                  {agencyHosts.map((host) => (
                    <div 
                      key={host.id || host.application_id} 
                      onClick={() => handleOpenHostDetails(host.id)}
                      className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 hover:bg-white/[0.04] transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-cyan-500/20 to-emerald-500/20 flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {host.avatar_url ? (
                            <img src={host.avatar_url} alt="" className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            (host.first_name || 'H').charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-white">
                              {host.first_name} {host.last_name}
                            </p>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/20">
                              Lv.{Number(host.current_level || 0)}
                            </span>
                          </div>
                          <p className="text-[11px] font-mono text-slate-400 mt-0.5">
                            ID: <span className="text-emerald-400 font-semibold">{host.xoxo_id || host.id?.substring(0, 8)}</span>
                          </p>
                        </div>
                      </div>

                      {/* Performance Pills */}
                      <div className="flex items-center gap-3 text-right">
                        <div className="bg-white/[0.02] px-3 py-1.5 rounded-xl border border-white/5">
                          <p className="text-[10px] text-slate-400 uppercase font-bold">Target Coins</p>
                          <p className="text-xs font-bold text-amber-400 font-mono">
                            {Number(host.monthly_target_coins || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white/[0.02] px-3 py-1.5 rounded-xl border border-white/5">
                          <p className="text-[10px] text-slate-400 uppercase font-bold">Valid Days</p>
                          <p className="text-xs font-bold text-white">
                            {host.valid_days || 0} / 30
                          </p>
                        </div>
                        <div className="bg-emerald-500/10 px-3 py-1.5 rounded-xl border border-emerald-500/20">
                          <p className="text-[10px] text-emerald-400 uppercase font-bold">Your BD Comm</p>
                          <p className="text-xs font-bold text-emerald-400 font-mono">
                            💎{Number(host.bd_comm_diamonds || 0).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTimingHost(host);
                            setIsTimingModalOpen(true);
                          }}
                          className="px-2.5 py-1.5 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 text-xs font-bold inline-flex items-center gap-1 transition cursor-pointer"
                          title="Edit Days & Timing"
                        >
                          <Clock size={12} />
                          <span>Edit</span>
                        </button>
                        <ArrowUpRight size={16} className="text-slate-500 group-hover:text-white shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DRILL-DOWN 2: SPECIFIC HOST CENTER DETAILS & LIVE SESSIONS */}
      {selectedHost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f1026] border border-emerald-500/30 rounded-[32px] p-6 md:p-8 max-w-2xl w-full shadow-2xl relative max-h-[90vh] flex flex-col">
            <button
              onClick={() => setSelectedHost(null)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>

            {/* Host Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-black text-xl shadow-lg shadow-emerald-500/20 shrink-0">
                {selectedHost.avatar_url ? (
                  <img src={selectedHost.avatar_url} alt="" className="w-full h-full object-cover rounded-2xl" />
                ) : (
                  (selectedHost.first_name || 'H').charAt(0).toUpperCase()
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-bold text-white">{selectedHost.first_name} {selectedHost.last_name}</h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-bold text-xs border border-purple-500/30">
                    Level {Number(selectedHost.current_level || 0)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                  <span>ID: <b className="text-emerald-400 font-mono">{selectedHost.xoxo_id || selectedHost.id?.substring(0, 8)}</b></span>
                  <span>•</span>
                  <span>Agency: <b className="text-white">{selectedHost.agency_name}</b></span>
                </div>
              </div>
            </div>

            {/* Host Center Numbers */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-3 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase">Achieved Coins</p>
                <p className="text-base font-black text-amber-400 mt-0.5 font-mono">
                  {Number(selectedHost.achieved_coins || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-3 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase">Valid Days</p>
                <p className="text-base font-black text-white mt-0.5">
                  {selectedHost.valid_days || 0} / 30
                </p>
              </div>
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-3 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase">Host Salary</p>
                <p className="text-base font-black text-cyan-400 mt-0.5 font-mono">
                  💎{Number(selectedHost.host_salary_diamonds || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-3 text-center">
                <p className="text-[10px] text-emerald-400 font-bold uppercase">Your BD Comm</p>
                <p className="text-base font-black text-emerald-400 mt-0.5 font-mono">
                  💎{Number(selectedHost.bd_commission_diamonds || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Live Streaming Sessions History */}
            <div className="flex-1 overflow-y-auto pr-1">
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Radio size={16} className="text-emerald-400 animate-pulse" />
                <span>Recent Live Streaming Sessions ({hostSessions.length})</span>
              </h4>

              {loadingHostDetail ? (
                <div className="text-center py-8 text-slate-400 text-xs">Loading sessions...</div>
              ) : hostSessions.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs bg-white/[0.01] rounded-2xl border border-white/5">
                  No live streaming sessions recorded this month.
                </div>
              ) : (
                <div className="space-y-2">
                  {hostSessions.map((sess) => (
                    <div key={sess.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-2 h-2 rounded-full ${sess.is_valid_day ? 'bg-emerald-400' : 'bg-slate-600'}`}></div>
                        <div>
                          <p className="font-bold text-white">
                            Session Duration: {sess.duration_mins || 0} mins
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {new Date(sess.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          sess.is_valid_day ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-slate-800 text-slate-400'
                        }`}>
                          {sess.is_valid_day ? 'Valid Day Achieved' : 'Normal Session'}
                        </span>
                        <p className="text-[10px] text-amber-400 font-mono mt-0.5">
                          {Number(sess.coins_earned || 0).toLocaleString()} Coins
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Alert */}
      {toastMsg && (
        <div className="fixed bottom-8 right-8 z-50 bg-emerald-500 text-white px-5 py-3 rounded-2xl shadow-2xl text-xs font-bold flex items-center gap-2 animate-in slide-in-from-bottom-4">
          <CheckCircle2 size={16} />
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  );
};

export default BDAgencies;
